import React from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import { devicesData, locationsData } from "../../utils/constants";

export default function DeviceDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const passedDevice = location.state?.device;
  
  // Check if this is a device model (like "MFR_node") or a serial number (like "MNLA4O10C")
  const deviceModel = devicesData.find(d => d.model === id);
  
  // If not a model, search for individual device instance
  let deviceInstance = null;
  let deviceLocation = null;
  
  if (!deviceModel && !passedDevice) {
    locationsData.forEach(loc => {
      const found = loc.devices.find(d => d.serialNumber === id);
      if (found) {
        deviceInstance = found;
        deviceLocation = loc;
      }
    });
  } else if (passedDevice) {
    deviceInstance = passedDevice;
    locationsData.forEach(loc => {
      if (loc.devices.some(d => d.serialNumber === passedDevice.serialNumber)) {
        deviceLocation = loc;
      }
    });
  }
  
  // Display device model page
  if (deviceModel) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <h1 className="text-2xl font-semibold mb-2">{deviceModel.name}</h1>
        <p className="text-gray-600 mb-4">Model: {deviceModel.model}</p>
        
        <div className="border rounded-lg p-6 bg-white">
          <h2 className="text-lg font-semibold mb-3">Description</h2>
          <p className="text-gray-700 mb-4">{deviceModel.fullDescription || deviceModel.description}</p>
          
          <h2 className="text-lg font-semibold mb-3 mt-6">Specifications</h2>
          <dl className="grid grid-cols-3 gap-4">
            <div>
              <dt className="text-gray-500">Manufacturer</dt>
              <dd className="font-medium">{deviceModel.manufacturer || "N/A"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Model</dt>
              <dd className="font-medium">{deviceModel.model}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Deployed Locations</dt>
              <dd className="font-medium">{deviceModel.locationCount}</dd>
            </div>
          </dl>
        </div>
      </div>
    );
  }
  
  // Display individual device instance page
  if (deviceInstance && deviceLocation) {
    const isMFR = deviceInstance.type === "MFR Node";
    
    const handleShowDataQuery = () => {
      const params = new URLSearchParams({
        location: deviceLocation.name,
        device_id: deviceInstance.serialNumber,
        start: '-7d',
        page: '0'
      });
      navigate(`/query-browser?${params.toString()}`);
    };
    
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-semibold">{deviceInstance.name}</h1>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleShowDataQuery}
          >
            Show Data Query Page
          </Button>
        </div>
        <p className="text-gray-600 mb-4">Serial Number: {deviceInstance.serialNumber}</p>
        
        <div className="border rounded-lg p-6 bg-white">
          <h2 className="text-lg font-semibold mb-3">Device Information</h2>
          <dl className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <dt className="text-gray-500">Type</dt>
              <dd className="font-medium">{deviceInstance.type}</dd>
            </div>
            <div>
              <dt className="text-gray-500">DevEUI</dt>
              <dd className="font-medium font-mono text-sm">{deviceInstance.devEUI}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Location</dt>
              <dd className="font-medium">
                <Link to={`/location/${deviceLocation.name}`} className="text-blue-600 hover:underline">
                  {deviceLocation.fullName} ({deviceLocation.name})
                </Link>
              </dd>
            </div>
            {isMFR && (
              <>
                <div>
                  <dt className="text-gray-500">Site Name</dt>
                  <dd className="font-medium">{deviceInstance.site || "-"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Attached Sensors</dt>
                  <dd className="font-medium">{deviceInstance.attachedSensors?.join(', ') || "-"}</dd>
                </div>
              </>
            )}
            {!isMFR && (
              <>
                <div>
                  <dt className="text-gray-500">Tree Species</dt>
                  <dd className="font-medium">{deviceInstance.treeSpecies || "-"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Tree Diameter (DBH)</dt>
                  <dd className="font-medium">{deviceInstance.treeDiameter || "-"}</dd>
                </div>
              </>
            )}
          </dl>
        </div>
      </div>
    );
  }
  
  // Not found
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <p className="text-red-600">Device not found.</p>
      <Link className="text-blue-600 underline" to="/devices">Back to Devices</Link>
    </div>
  );
}